import { useState, useEffect } from 'react'
import AdminLayout from '../components/AdminLayout'
import { supabase } from '../services/supabase'

interface Product {
  id: string
  name: string
  description: string
  price: number
  stock: number
  category: string
  image_urls: string[]
  created_at: string
  low_stock_threshold: number
  low_stock_notified: boolean
}

interface ProductVariant {
  id: string
  product_id: string
  variant_name: string
  variant_value: string
  price: number
  stock_quantity: number
  created_at: string
  low_stock_threshold: number
  low_stock_notified: boolean
}

interface VariantFormData {
  id?: string
  variant_name: string
  variant_value: string
  price: string
  stock_quantity: string
  low_stock_threshold: string
}

export default function Admin() {
  const [products, setProducts] = useState<Product[]>([])
  const [productsLoading, setProductsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [categories, setCategories] = useState<string[]>([])

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [price, setPrice] = useState('')
  const [stock, setStock] = useState('')
  const [lowStockThreshold, setLowStockThreshold] = useState('5')
  const [category, setCategory] = useState('')
  const [selectedImages, setSelectedImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Variant state
  const [variants, setVariants] = useState<VariantFormData[]>([])
  const [existingVariantIds, setExistingVariantIds] = useState<string[]>([])

  useEffect(() => {
    fetchProducts()
    fetchCategories()
  }, [])

  const fetchProducts = async () => {
    try {
      setProductsLoading(true)

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      const response = await fetch(`${supabaseUrl}/rest/v1/products?select=*&order=created_at.desc`, {
        method: 'GET',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setProducts(data || [])
    } catch (error) {
      console.error('Error fetching products:', error)
      setProducts([])
    } finally {
      setProductsLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      const response = await fetch(`${supabaseUrl}/rest/v1/categories?select=name&order=name.asc`, {
        method: 'GET',
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`,
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      const categoryNames = data.map((cat: { name: string }) => cat.name)
      setCategories(categoryNames)

      if (categoryNames.length > 0 && !category) {
        setCategory(categoryNames[0])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newFiles = Array.from(files)
    const totalImages = selectedImages.length + newFiles.length

    if (totalImages > 4) {
      setMessage({ type: 'error', text: 'You can only upload up to 4 images per product' })
      return
    }

    const validFiles = newFiles.filter(file => {
      const isImage = file.type.startsWith('image/')
      const isValidSize = file.size <= 5 * 1024 * 1024

      if (!isImage) {
        setMessage({ type: 'error', text: `${file.name} is not an image file` })
        return false
      }
      if (!isValidSize) {
        setMessage({ type: 'error', text: `${file.name} is too large (max 5MB)` })
        return false
      }
      return true
    })

    setSelectedImages([...selectedImages, ...validFiles])

    validFiles.forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result as string])
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index))
    setImagePreviews(prev => prev.filter((_, i) => i !== index))
  }

  const removeExistingImage = (index: number) => {
    setExistingImageUrls(prev => prev.filter((_, i) => i !== index))
  }

  // Variant management functions
  const addVariant = () => {
    setVariants(prev => [...prev, {
      variant_name: '',
      variant_value: '',
      price: '',
      stock_quantity: '',
      low_stock_threshold: '5'
    }])
  }

  const removeVariant = (index: number) => {
    setVariants(prev => prev.filter((_, i) => i !== index))
  }

  const updateVariant = (index: number, field: keyof VariantFormData, value: string) => {
    setVariants(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v))
  }

  const fetchProductVariants = async (productId: string): Promise<ProductVariant[]> => {
    try {
      const { data, error } = await supabase
        .from('product_variants')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching variants:', error)
      return []
    }
  }

  const openAddModal = () => {
    setEditingProduct(null)
    setName('')
    setDescription('')
    setPrice('')
    setStock('')
    setLowStockThreshold('5')
    setSelectedImages([])
    setImagePreviews([])
    setExistingImageUrls([])
    setVariants([])
    setExistingVariantIds([])
    setMessage(null)

    if (categories.length > 0) {
      setCategory(categories[0])
    }

    setIsModalOpen(true)

    // Fetch categories in background to ensure latest data
    fetchCategories()
  }

  const openEditModal = async (product: Product) => {
    setEditingProduct(product)
    setName(product.name)
    setDescription(product.description)
    setPrice(product.price.toString())
    setStock(product.stock.toString())
    setLowStockThreshold(product.low_stock_threshold?.toString() || '5')
    setCategory(product.category)
    setSelectedImages([])
    setImagePreviews([])
    setExistingImageUrls(product.image_urls || [])
    setMessage(null)

    // Fetch existing variants for this product
    const productVariants = await fetchProductVariants(product.id)
    const variantFormData: VariantFormData[] = productVariants.map(v => ({
      id: v.id,
      variant_name: v.variant_name,
      variant_value: v.variant_value,
      price: v.price.toString(),
      stock_quantity: v.stock_quantity.toString(),
      low_stock_threshold: v.low_stock_threshold?.toString() || '5'
    }))
    setVariants(variantFormData)
    setExistingVariantIds(productVariants.map(v => v.id))

    setIsModalOpen(true)

    // Fetch categories in background to ensure latest data
    fetchCategories()
  }

  const handleDeleteProduct = async (product: Product) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this product? This cannot be undone.'
    )

    if (!confirmed) return

    try {
      setProductsLoading(true)

      if (product.image_urls && product.image_urls.length > 0) {
        for (const imageUrl of product.image_urls) {
          try {
            const urlParts = imageUrl.split('/product-images/')
            if (urlParts.length === 2) {
              const filePath = urlParts[1]
              const { error: storageError } = await supabase.storage
                .from('product-images')
                .remove([filePath])

              if (storageError) {
                console.error('Error deleting image from storage:', storageError)
              }
            }
          } catch (error) {
            console.error('Error processing image deletion:', error)
          }
        }
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', product.id)

      if (error) throw error

      setProducts(products.filter(p => p.id !== product.id))

      setMessage({ type: 'success', text: 'Product deleted successfully!' })

      setTimeout(() => {
        setMessage(null)
      }, 3000)
    } catch (error) {
      console.error('Error deleting product:', error)
      setMessage({ type: 'error', text: 'Failed to delete product. Please try again.' })

      setTimeout(() => {
        setMessage(null)
      }, 3000)
    } finally {
      setProductsLoading(false)
    }
  }

  const uploadImages = async (): Promise<string[]> => {
    const uploadedUrls: string[] = []

    for (const file of selectedImages) {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`
      const filePath = `${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file)

      if (uploadError) {
        throw uploadError
      }

      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath)

      uploadedUrls.push(data.publicUrl)
    }

    return uploadedUrls
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setLoading(true)

    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Product name is required' })
      setLoading(false)
      return
    }

    if (!description.trim()) {
      setMessage({ type: 'error', text: 'Product description is required' })
      setLoading(false)
      return
    }

    const priceNum = parseFloat(price)
    if (isNaN(priceNum) || priceNum <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid price greater than 0' })
      setLoading(false)
      return
    }

    const stockNum = parseInt(stock)
    if (isNaN(stockNum) || stockNum < 0) {
      setMessage({ type: 'error', text: 'Please enter a valid stock quantity (0 or greater)' })
      setLoading(false)
      return
    }

    const totalImages = selectedImages.length + existingImageUrls.length
    if (totalImages === 0) {
      setMessage({ type: 'error', text: 'Please upload at least 1 product image' })
      setLoading(false)
      return
    }

    // Validate variants if any exist
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i]
      if (!v.variant_name.trim()) {
        setMessage({ type: 'error', text: `Variant ${i + 1}: Name is required` })
        setLoading(false)
        return
      }
      if (!v.variant_value.trim()) {
        setMessage({ type: 'error', text: `Variant ${i + 1}: Value is required` })
        setLoading(false)
        return
      }
      const variantPrice = parseFloat(v.price)
      if (isNaN(variantPrice) || variantPrice <= 0) {
        setMessage({ type: 'error', text: `Variant ${i + 1}: Price must be greater than 0` })
        setLoading(false)
        return
      }
      const variantStock = parseInt(v.stock_quantity)
      if (isNaN(variantStock) || variantStock < 0) {
        setMessage({ type: 'error', text: `Variant ${i + 1}: Stock must be 0 or greater` })
        setLoading(false)
        return
      }
    }

    try {
      const newImageUrls = selectedImages.length > 0 ? await uploadImages() : []

      const allImageUrls = [...existingImageUrls, ...newImageUrls]

      let productId: string

      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update({
            name: name.trim(),
            description: description.trim(),
            price: priceNum,
            stock: stockNum,
            low_stock_threshold: parseInt(lowStockThreshold) || 5,
            category: category,
            image_urls: allImageUrls,
          })
          .eq('id', editingProduct.id)

        if (error) throw error

        productId = editingProduct.id

        // Handle variants for existing product
        // 1. Delete removed variants
        const currentVariantIds = variants.filter(v => v.id).map(v => v.id as string)
        const variantsToDelete = existingVariantIds.filter(id => !currentVariantIds.includes(id))

        if (variantsToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('product_variants')
            .delete()
            .in('id', variantsToDelete)

          if (deleteError) throw deleteError
        }

        // 2. Update existing variants
        for (const variant of variants.filter(v => v.id)) {
          const { error: updateError } = await supabase
            .from('product_variants')
            .update({
              variant_name: variant.variant_name.trim(),
              variant_value: variant.variant_value.trim(),
              price: parseFloat(variant.price),
              stock_quantity: parseInt(variant.stock_quantity),
              low_stock_threshold: parseInt(variant.low_stock_threshold) || 5
            })
            .eq('id', variant.id)

          if (updateError) throw updateError
        }

        // 3. Insert new variants
        const newVariants = variants.filter(v => !v.id)
        if (newVariants.length > 0) {
          const variantsToInsert = newVariants.map(v => ({
            product_id: productId,
            variant_name: v.variant_name.trim(),
            variant_value: v.variant_value.trim(),
            price: parseFloat(v.price),
            stock_quantity: parseInt(v.stock_quantity),
            low_stock_threshold: parseInt(v.low_stock_threshold) || 5
          }))

          const { error: insertError } = await supabase
            .from('product_variants')
            .insert(variantsToInsert)

          if (insertError) throw insertError
        }

        setMessage({ type: 'success', text: 'Product updated successfully!' })
      } else {
        // Create new product
        const { data: newProduct, error } = await supabase
          .from('products')
          .insert([
            {
              name: name.trim(),
              description: description.trim(),
              price: priceNum,
              stock: stockNum,
              low_stock_threshold: parseInt(lowStockThreshold) || 5,
              category: category,
              image_urls: allImageUrls,
            },
          ])
          .select('id')
          .single()

        if (error) throw error

        productId = newProduct.id

        // Insert variants for new product
        if (variants.length > 0) {
          const variantsToInsert = variants.map(v => ({
            product_id: productId,
            variant_name: v.variant_name.trim(),
            variant_value: v.variant_value.trim(),
            price: parseFloat(v.price),
            stock_quantity: parseInt(v.stock_quantity),
            low_stock_threshold: parseInt(v.low_stock_threshold) || 5
          }))

          const { error: variantError } = await supabase
            .from('product_variants')
            .insert(variantsToInsert)

          if (variantError) throw variantError
        }

        setMessage({ type: 'success', text: 'Product added successfully!' })
      }

      setName('')
      setDescription('')
      setPrice('')
      setStock('')
      setCategory(categories.length > 0 ? categories[0] : '')
      setSelectedImages([])
      setImagePreviews([])
      setExistingImageUrls([])
      setVariants([])
      setExistingVariantIds([])
      setEditingProduct(null)

      await fetchProducts()

      setTimeout(() => {
        setIsModalOpen(false)
        setMessage(null)
      }, 1500)
    } catch (error) {
      console.error('Error saving product:', error)
      setMessage({ type: 'error', text: `Failed to ${editingProduct ? 'update' : 'add'} product. Please try again.` })
    } finally {
      setLoading(false)
    }
  }

  const getStockBadge = (stockCount: number, threshold: number = 5) => {
    if (stockCount === 0) {
      return <span className="badge-sold-out">Sold Out</span>
    } else if (stockCount <= threshold) {
      return <span className="badge-low-stock">Low Stock</span>
    }
    return <span className="badge-in-stock">In Stock</span>
  }

  return (
    <>
      {/* Add/Edit Product Modal - Rendered first for proper stacking */}
      {isModalOpen && (
        <div
          data-testid="modal-backdrop"
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false)
              setMessage(null)
            }
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false)
                  setMessage(null)
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" style={{ width: '20px', height: '20px', maxWidth: '20px', maxHeight: '20px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {message && (
                <div className={`mb-5 p-4 rounded-lg text-sm ${
                  message.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {message.text}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Product Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="e.g., Wireless Headphones"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="Describe your product..."
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Price ($)
                    </label>
                    <input
                      type="number"
                      id="price"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      step="0.01"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="29.99"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="stock" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Stock
                    </label>
                    <input
                      type="number"
                      id="stock"
                      value={stock}
                      onChange={(e) => setStock(e.target.value)}
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                      placeholder="100"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Low Stock Alert Threshold
                  </label>
                  <input
                    type="number"
                    id="lowStockThreshold"
                    value={lowStockThreshold}
                    onChange={(e) => setLowStockThreshold(e.target.value)}
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="5"
                    disabled={loading}
                  />
                  <p className="mt-1 text-xs text-gray-400">Alert when stock falls to or below this number</p>
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Category
                  </label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    disabled={loading}
                  >
                    {categories.length === 0 ? (
                      <option value="">No categories available</option>
                    ) : (
                      categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Product Images <span className="text-gray-400 font-normal">(1-4 images, max 5MB each)</span>
                  </label>

                  {existingImageUrls.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-2">Current images:</p>
                      <div className="flex flex-wrap gap-3">
                        {existingImageUrls.map((url, index) => (
                          <div key={`existing-${index}`} className="relative group">
                            <div className="w-16 h-16 rounded-lg border-2 border-purple-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                              <img src={url} alt={`Existing ${index + 1}`} className="img-admin-preview" style={{ width: '56px', height: '56px', maxWidth: '56px', maxHeight: '56px' }} />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              disabled={loading}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3" style={{ width: '12px', height: '12px', maxWidth: '12px', maxHeight: '12px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {imagePreviews.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-400 mb-2">New images:</p>
                      <div className="flex flex-wrap gap-3">
                        {imagePreviews.map((preview, index) => (
                          <div key={`new-${index}`} className="relative group">
                            <div className="w-16 h-16 rounded-lg border-2 border-green-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                              <img src={preview} alt={`Preview ${index + 1}`} className="img-admin-preview" style={{ width: '56px', height: '56px', maxWidth: '56px', maxHeight: '56px' }} />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              disabled={loading}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3" style={{ width: '12px', height: '12px', maxWidth: '12px', maxHeight: '12px' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(existingImageUrls.length + selectedImages.length) < 4 && (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400 mb-1" style={{ width: '24px', height: '24px', maxWidth: '24px', maxHeight: '24px' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                        </svg>
                        <p className="text-xs text-gray-500">
                          Click to upload ({existingImageUrls.length + selectedImages.length}/4)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        disabled={loading}
                      />
                    </label>
                  )}
                </div>

                {/* Product Variants Section */}
                <div className="border-t border-gray-200 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Product Variants <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <button
                      type="button"
                      onClick={addVariant}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Add Variant
                    </button>
                  </div>

                  {variants.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No variants added. Add variants for products with different sizes, colors, etc.</p>
                  ) : (
                    <div className="space-y-3">
                      {variants.map((variant, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Variant Type</label>
                                <input
                                  type="text"
                                  value={variant.variant_name}
                                  onChange={(e) => updateVariant(index, 'variant_name', e.target.value)}
                                  placeholder="e.g., Size, Color"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  disabled={loading}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Value</label>
                                <input
                                  type="text"
                                  value={variant.variant_value}
                                  onChange={(e) => updateVariant(index, 'variant_value', e.target.value)}
                                  placeholder="e.g., Small, Red"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  disabled={loading}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Price ($)</label>
                                <input
                                  type="number"
                                  value={variant.price}
                                  onChange={(e) => updateVariant(index, 'price', e.target.value)}
                                  placeholder="29.99"
                                  step="0.01"
                                  min="0"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  disabled={loading}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Stock</label>
                                <input
                                  type="number"
                                  value={variant.stock_quantity}
                                  onChange={(e) => updateVariant(index, 'stock_quantity', e.target.value)}
                                  placeholder="50"
                                  min="0"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  disabled={loading}
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">Low Stock Alert Threshold</label>
                                <input
                                  type="number"
                                  value={variant.low_stock_threshold}
                                  onChange={(e) => updateVariant(index, 'low_stock_threshold', e.target.value)}
                                  placeholder="5"
                                  min="0"
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  disabled={loading}
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeVariant(index)}
                              disabled={loading}
                              className="mt-5 p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove variant"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                  >
                    {loading
                      ? (editingProduct ? 'Updating...' : 'Adding...')
                      : (editingProduct ? 'Update Product' : 'Add Product')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <AdminLayout>
        <div className="max-w-7xl">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Product List</h1>
            <p className="text-gray-500 text-sm">Track stock levels, availability, and restocking needs in real time.</p>
          </div>

          {/* Products Section */}
          <div>
            {/* Success/Error Message */}
            {message && !isModalOpen && (
              <div className={`mb-5 p-4 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {message.text}
              </div>
            )}

            {/* Header with Add Button */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <button
                  onClick={openAddModal}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4" style={{ width: '16px', height: '16px', maxWidth: '16px', maxHeight: '16px', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Product
                </button>
              </div>
            </div>

            {productsLoading ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-500 text-sm">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-16 text-center">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-400" style={{ width: '24px', height: '24px', maxWidth: '24px', maxHeight: '24px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products yet</h3>
                <p className="text-gray-500 text-sm mb-6">Get started by adding your first product</p>
                <button
                  onClick={openAddModal}
                  className="px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Add Your First Product
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all"
                  >
                    {/* Product Image - STRICTLY CONSTRAINED */}
                    <div className="h-28 bg-gray-50 flex items-center justify-center p-2 border-b border-gray-100 overflow-hidden">
                      {product.image_urls && product.image_urls.length > 0 ? (
                        <img
                          src={product.image_urls[0]}
                          alt={product.name}
                          className="img-admin-thumbnail"
                          style={{ width: '72px', height: '72px', maxWidth: '72px', maxHeight: '72px' }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-6 h-6 text-gray-300" style={{ width: '24px', height: '24px', maxWidth: '24px', maxHeight: '24px' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
                      <p className="text-xs text-gray-400 mb-3">{product.category}</p>

                      <div className="flex items-center justify-between mb-4">
                        <p className="text-lg font-bold text-gray-900">${product.price.toFixed(2)}</p>
                        {getStockBadge(product.stock, product.low_stock_threshold)}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(product)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-50 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5" style={{ width: '14px', height: '14px', maxWidth: '14px', maxHeight: '14px', flexShrink: 0 }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors border border-red-200"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5" style={{ width: '14px', height: '14px', maxWidth: '14px', maxHeight: '14px', flexShrink: 0 }}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </AdminLayout>
    </>
  )
}
